import { Form, Input, Modal, Select, Spin, AutoComplete } from "antd";
import React, { useCallback, useRef, useState } from "react";
import DataProvider from "../../apis";
import type { DrugItem } from "../../apis";
import Colors from "../../constants/Colors";

// Extracted out of E-prescription/create-eprescription.tsx's own inline
// "Add medication" modal — the doctor report flow (appointments/
// doctor-report.tsx) needs the exact same medicine-search + fields, and a
// second hand-copied modal would drift from this one over time. Both
// screens now render this component instead.

const MEDICINE_SEARCH_DEBOUNCE_MS = 300;

const ROUTE_OPTIONS = [
  { value: "Oral", label: "Oral" },
  { value: "Sublingual", label: "Sublingual" },
  { value: "Buccal", label: "Buccal" },
  { value: "Topical", label: "Topical" },
  { value: "Cream", label: "Cream" },
  { value: "Ointment", label: "Ointment" },
  { value: "Transdermal", label: "Transdermal (Patch)" },
  { value: "Nasal", label: "Nasal" },
  { value: "Inhalation", label: "Inhalation" },
  { value: "Ophthalmic", label: "Ophthalmic (Eye drops)" },
  { value: "Otic", label: "Otic (Ear drops)" },
  { value: "Anal", label: "Anal" },
  { value: "Rectal", label: "Rectal" },
  { value: "Vaginal", label: "Vaginal" },
  { value: "Injection", label: "Injection" },
  { value: "Intravenous", label: "Intravenous (IV)" },
  { value: "Intramuscular", label: "Intramuscular (IM)" },
  { value: "Subcutaneous", label: "Subcutaneous (SC)" },
  { value: "Intradermal", label: "Intradermal (ID)" },
  { value: "Intrathecal", label: "Intrathecal" },
  { value: "Epidural", label: "Epidural" },
];

export type MedicationFormValues = {
  medicine?: string;
  dose?: string;
  route?: string;
  dosage?: string;
  period?: string;
};

interface AddMedicationModalProps {
  open: boolean;
  onCancel: () => void;
  onSubmit: (values: MedicationFormValues) => void;
  /** Pre-fills the form for editing an already-drafted line item instead
   * of adding a new one — used by the doctor report's medication list
   * (still editable up until the e-prescription is actually generated). */
  initialValues?: MedicationFormValues;
}

export default function AddMedicationModal({ open, onCancel, onSubmit, initialValues }: AddMedicationModalProps) {
  const [form] = Form.useForm<MedicationFormValues>();
  const [medicineOptions, setMedicineOptions] = useState<{ value: string; label: string }[]>([]);
  const [medicineSearchLoading, setMedicineSearchLoading] = useState(false);
  const medicineSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isEditing = !!initialValues;

  const handleMedicineSearch = useCallback((query: string) => {
    if (medicineSearchTimerRef.current) {
      clearTimeout(medicineSearchTimerRef.current);
      medicineSearchTimerRef.current = null;
    }
    if (!query || query.trim().length < 2) {
      setMedicineOptions([]);
      return;
    }
    medicineSearchTimerRef.current = setTimeout(() => {
      setMedicineSearchLoading(true);
      DataProvider.searchDrugs(query)
        .then((res) => {
          const raw = res.data;
          const list = Array.isArray(raw) ? raw : (raw as { data?: DrugItem[] })?.data ?? [];
          setMedicineOptions((list as DrugItem[]).map((d) => ({ value: d.name, label: d.name })));
        })
        .catch(() => setMedicineOptions([]))
        .finally(() => {
          setMedicineSearchLoading(false);
          medicineSearchTimerRef.current = null;
        });
    }, MEDICINE_SEARCH_DEBOUNCE_MS);
  }, []);

  const resetLocalState = () => {
    setMedicineOptions([]);
    if (medicineSearchTimerRef.current) {
      clearTimeout(medicineSearchTimerRef.current);
      medicineSearchTimerRef.current = null;
    }
  };

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        onSubmit(values);
        form.resetFields();
        resetLocalState();
      })
      .catch(() => {});
  };

  const handleCancel = () => {
    form.resetFields();
    resetLocalState();
    onCancel();
  };

  return (
    <Modal
      title={isEditing ? "Edit medication" : "Add medication"}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      okText={isEditing ? "Save" : "Add"}
      cancelText="Cancel"
      width={400}
      destroyOnClose
      // Tailwind's preflight reset (@tailwind base, active in this app)
      // strips antd's default primary-button background, so the OK
      // button renders with no visible fill unless explicitly overridden
      // — same reason every other primary CTA in this app sets this
      // inline (e.g. create-eprescription.tsx's submit button) instead
      // of relying on antd's own styling.
      okButtonProps={{ style: { background: Colors.primary, borderColor: Colors.primary } }}
    >
      <Form layout="vertical" form={form} initialValues={initialValues}>
        <Form.Item<MedicationFormValues>
          label="Medicine"
          name="medicine"
          rules={[{ required: true, message: "Required" }]}
        >
          <AutoComplete
            placeholder="Type to search medications or enter custom name"
            options={medicineOptions}
            onSearch={handleMedicineSearch}
            notFoundContent={
              medicineSearchLoading ? (
                <div className="py-2 text-center text-gray-500">
                  <Spin size="small" />
                </div>
              ) : (
                "Type at least 2 characters to search"
              )
            }
            filterOption={false}
            allowClear
            aria-label="Search or enter medication name"
          />
        </Form.Item>
        <Form.Item<MedicationFormValues>
          label="Dose"
          name="dose"
          rules={[{ required: true, message: "Required" }]}
        >
          <Input placeholder="e.g. 500mg" />
        </Form.Item>
        <Form.Item<MedicationFormValues>
          label="Route"
          name="route"
          rules={[{ required: true, message: "Required" }]}
        >
          <Select
            showSearch
            placeholder="Select a route"
            options={ROUTE_OPTIONS}
            optionFilterProp="label"
            allowClear
          />
        </Form.Item>
        <Form.Item<MedicationFormValues>
          label="Dosage"
          name="dosage"
          rules={[{ required: true, message: "Required" }]}
        >
          <Input placeholder="e.g. 1 tablet twice daily" />
        </Form.Item>
        <Form.Item<MedicationFormValues>
          label="Period"
          name="period"
          rules={[{ required: true, message: "Required" }]}
        >
          <Input placeholder="e.g. 7 days" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
